import { Button } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Sider from 'antd/lib/layout/Sider';
import { useTranslation } from 'react-i18next';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Filter } from '@common/models';

import FilterItem from './components/FilterItem';

import './filter.scss';

export interface FiltersProps<T> {
    isFilterExpanded: boolean;
    setFilterExpanded: (value: boolean) => void;
    filterOptions: T;
    updateFilters: (filters: T) => void;
}

/**
 * Represents the ui filters component.
 * The component will work as much as the provided generic type T will be Record<StringKeys,Filter>.
 * Basically the T object is an object similar to a Map. The 'StringKeys' are the headlines for the filters sections
 * and the Filter it's an object containing all the needed information about a filter section.
 * @param props
 */
export const Filters = <T extends Record<string, Filter>>({
    isFilterExpanded,
    setFilterExpanded,
    filterOptions,
    updateFilters,
}: FiltersProps<T>): JSX.Element => {
    const { t } = useTranslation();

    const expandFilters = (value: boolean): void => {
        setFilterExpanded(value);
    };

    const clearFilters = () => {
        const draftFilterOptions = Object.entries(filterOptions).reduce((acc, [key, filter]) => {
            const updatedFilterOptions = filter?.filterOptions.map((filterOption) => {
                return {
                    ...filterOption,
                    isSelected: false,
                };
            });
            return {
                ...acc,
                [key]: {
                    ...filter,
                    filterOptions: updatedFilterOptions,
                },
            };
        }, {});

        updateFilters(draftFilterOptions as T);
    };

    const selectFilter = (e: CheckboxChangeEvent) => {
        const draftFilterOptions = Object.entries(filterOptions).reduce((acc, [key, filter]) => {
            const updatedFilterOptions = filter?.filterOptions.map((filterOption) => {
                return {
                    ...filterOption,
                    isSelected:
                        filterOption.name !== e.target.value ? filterOption.isSelected : !filterOption.isSelected,
                };
            });
            return {
                ...acc,
                [key]: {
                    ...filter,
                    filterOptions: updatedFilterOptions,
                },
            };
        }, {});

        updateFilters(draftFilterOptions as T);
    };

    const handleFilterSection = (key: keyof T) => {
        if (filterOptions[key]) {
            updateFilters({
                ...filterOptions,
                [key]: {
                    ...filterOptions[key],
                    isExpanded: !filterOptions[key].isExpanded,
                },
            });
        }
    };

    return isFilterExpanded ? (
        <Sider
            className="filters "
            onCollapse={() => expandFilters(false)}
            theme="light"
            breakpoint="sm"
            width={250}
            collapsedWidth={0}
            collapsible
            trigger={<LeftOutlined />}
        >
            <Button className="clear-filters-btn" onClick={clearFilters}>
                {t('COMMON.CLEAR_FILTERS')}
            </Button>
            {[...Object.entries(filterOptions)]
                .sort(([, a], [, b]) => a.position - b.position)
                .map(([filterName, filter], index) => (
                    <div className="filters-section" key={index}>
                        <FilterItem
                            key={index}
                            headline={filterName}
                            filter={filter}
                            handleFilterSection={handleFilterSection}
                            selectFilter={selectFilter}
                        />
                    </div>
                ))}
        </Sider>
    ) : (
        <Button className="apply-filters-btn" onClick={() => expandFilters(true)}>
            {t('COMMON.APPLY_FILTERS')}
            <span className="expand-arrow">
                <RightOutlined />
            </span>
        </Button>
    );
};
